import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import {
  fetchBundleHistory,
  fetchBundles,
  saveBundle,
  type BundleChange,
  type BundlesOverview,
  type FeatureBundle,
  type ReleaseProfile,
} from './featureFlagsApi';

/**
 * Paczki wydania na ekranie „Przełączniki funkcji” (25.10.2026, S-25.10-02;
 * decyzja użytkownika 24.10.2026: „wydajemy przed pilotażem i całą paczkę
 * naraz, ale tylko administracja i CS, żeby nie było tam nic innego i żeby
 * można to było łatwo ukryć z panelu admina”).
 *
 * Jeden włącznik na paczkę: włącza i wyłącza naraz wszystkie jej funkcje,
 * z powodem (historia i dziennik działań). Opcja „ukryj też z menu” chowa
 * przy wyłączonej paczce jej dotychczasowe ekrany. Działa tylko w profilu
 * wydania (konfiguracja serwera) — poza nim ekran mówi, że zapis niczego nie
 * przełącza. Zmienia wyłącznie administrator (API: 403).
 */

/** Najdłuższy powód — jak przy przełącznikach (`MAX_REASON_LENGTH` w API). */
export const MAX_BUNDLE_REASON = 500;

/** Wartości pola `field` historii — dane z API. */
const FIELD_LABELS: Record<string, (v: boolean | null) => string> = {
  enabled: (v) => (v === null ? 'wyłączona (domyślnie)' : v ? 'włączona' : 'wyłączona'),
  hideMenuWhenOff: (v) => (v ? 'ukrywa ekrany z menu' : 'menu bez zmian'),
};

/** Nazwy ekranów z menu; zasób spoza menu — sama nazwa zasobu. */
export function releasedScreensText(resources: readonly string[]): string {
  return resources
    .map((r) => {
      try {
        return menuLabel(r);
      } catch {
        return r;
      }
    })
    .join(', ');
}

/** Zdanie o profilu dla paczki — co znaczy włącznik na tym środowisku. */
export function profileNote(
  profile: ReleaseProfile,
  bundle: FeatureBundle,
): { severity: 'info' | 'warning'; text: string } {
  if (!profile.active) {
    return {
      severity: 'info',
      text: 'Na tym środowisku nie działa profil wydania: włącznik zapisuje się, ale niczego nie przełącza — funkcje działają według przełączników poniżej, a wybór wersji jak dotąd.',
    };
  }
  if (!bundle.inProfile) {
    return {
      severity: 'warning',
      text: 'Ta paczka nie jest wydawana na tym środowisku — jej funkcje są wyłączone bez względu na włącznik.',
    };
  }
  return {
    severity: 'info',
    text: 'Na tym środowisku działa profil wydania: działają wyłącznie funkcje wydawanych paczek, i tylko przy włączonej paczce. Pozostałe funkcje są wyłączone dla wszystkich, a wyboru wersji nie ma.',
  };
}

type PendingChange = {
  bundle: FeatureBundle;
  change: { enabled?: boolean; hideMenuWhenOff?: boolean };
  title: string;
};

export const FeatureBundlesPanel = ({ canEdit, onChanged }: { canEdit: boolean | null; onChanged?: () => void }) => {
  const notify = useNotify();
  const [overview, setOverview] = useState<BundlesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [revision, setRevision] = useState(0);

  const load = useCallback(async () => {
    try {
      setOverview(await fetchBundles());
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Paczki wydania: {error}
      </Alert>
    );
  }
  if (overview === null) return <CircularProgress size={20} sx={{ mb: 2 }} />;

  return (
    <Box sx={{ mb: 3 }} data-testid="feature-bundles">
      <Typography variant="h6" sx={{ mb: 1 }}>
        Paczki wydania
      </Typography>
      {overview.profile.unknownBundles.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="bundle-unknown">
          W konfiguracji serwera są nieznane paczki: {overview.profile.unknownBundles.join(', ')} — niczego nie
          włączają.
        </Alert>
      )}
      {overview.bundles.map((b) => (
        <BundleCard
          key={b.key}
          bundle={b}
          profile={overview.profile}
          canEdit={canEdit === true}
          revision={revision}
          onAsk={(change, title) => setPending({ bundle: b, change, title })}
        />
      ))}
      {pending && canEdit === true && (
        <BundleReasonDialog
          pending={pending}
          onClose={() => setPending(null)}
          onSaved={(next) => {
            setPending(null);
            setOverview(next);
            setRevision((r) => r + 1);
            notify('Paczka zapisana', { type: 'success' });
            onChanged?.();
          }}
        />
      )}
    </Box>
  );
};

function BundleCard({
  bundle,
  profile,
  canEdit,
  revision,
  onAsk,
}: {
  bundle: FeatureBundle;
  profile: ReleaseProfile;
  canEdit: boolean;
  revision: number;
  onAsk: (change: { enabled?: boolean; hideMenuWhenOff?: boolean }, title: string) => void;
}) {
  const [showFlags, setShowFlags] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const note = profileNote(profile, bundle);
  const screens = releasedScreensText(bundle.releasedMenuResources);
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1 }} data-testid={`bundle-${bundle.key}`}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <AssignmentTurnedInOutlined
          color={bundle.enabled ? 'success' : 'disabled'}
          sx={{ fontSize: 36, mt: 0.5 }}
          aria-hidden
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {bundle.label}
            </Typography>
            <Chip
              size="small"
              color={bundle.enabled ? 'success' : 'default'}
              label={bundle.enabled ? 'włączona' : bundle.configured ? 'wyłączona' : 'wyłączona (domyślnie)'}
              data-testid={`bundle-state-${bundle.key}`}
            />
            {!bundle.enabled && bundle.hideMenuWhenOff && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="ekrany ukryte z menu"
                data-testid={`bundle-hidden-${bundle.key}`}
              />
            )}
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {bundle.description}
          </Typography>
          <Alert severity={note.severity} sx={{ mt: 1 }} data-testid={`bundle-profile-${bundle.key}`}>
            {note.text}
          </Alert>
          {bundle.lastChange && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Ostatnia zmiana: {fmtDate(bundle.lastChange.changedAt)} · {bundle.lastChange.changedByName ?? '—'} ·
              powód: {bundle.lastChange.reason}
            </Typography>
          )}
          <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                color={bundle.enabled ? 'inherit' : 'primary'}
                data-testid={`bundle-toggle-${bundle.key}`}
                onClick={() =>
                  onAsk(
                    { enabled: !bundle.enabled },
                    bundle.enabled ? `Wyłącz paczkę „${bundle.label}”` : `Włącz paczkę „${bundle.label}”`,
                  )
                }
              >
                {bundle.enabled ? 'Wyłącz paczkę' : 'Włącz paczkę'}
              </Button>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={bundle.hideMenuWhenOff}
                  disabled={!canEdit}
                  slotProps={{
                    input: {
                      'aria-label': 'Ukryj też z menu przy wyłączonej paczce',
                    },
                  }}
                  data-testid={`bundle-hide-menu-${bundle.key}`}
                  onChange={(e) =>
                    onAsk(
                      { hideMenuWhenOff: e.target.checked },
                      e.target.checked
                        ? 'Ukryj też ekrany z menu przy wyłączonej paczce'
                        : 'Pokazuj ekrany w menu przy wyłączonej paczce',
                    )
                  }
                />
              }
              label={<Typography variant="body2">Przy wyłączonej paczce ukryj też z menu: {screens}</Typography>}
            />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Button size="small" aria-expanded={showFlags} onClick={() => setShowFlags((v) => !v)}>
              Funkcje w paczce ({bundle.flags.length})
            </Button>
            <Button size="small" aria-expanded={showHistory} onClick={() => setShowHistory((v) => !v)}>
              Historia
            </Button>
          </Stack>
          {showFlags && (
            <Table size="small" aria-label="Funkcje w paczce" data-testid={`bundle-flags-${bundle.key}`}>
              <TableBody>
                {bundle.flags.map((f) => (
                  <TableRow key={f.key}>
                    <TableCell sx={{ width: '18%', whiteSpace: 'nowrap' }}>
                      {f.presentationItems.join(', ') || '—'}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{f.label}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {showHistory && <BundleHistory bundleKey={bundle.key} revision={revision} />}
        </Box>
      </Stack>
    </Paper>
  );
}

function BundleHistory({ bundleKey, revision }: { bundleKey: string; revision: number }) {
  const [rows, setRows] = useState<BundleChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    fetchBundleHistory(bundleKey)
      .then((r) => {
        if (active) setRows(r);
      })
      .catch((e) => {
        if (active) setError(toErrorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [bundleKey, revision]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (rows === null) return <CircularProgress size={16} />;
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" component="div">
        Bez zmian — paczka wyłączona (domyślnie).
      </Typography>
    );
  }
  return (
    <Table
      size="small"
      aria-label="Historia paczki"
      sx={{ tableLayout: 'fixed' }}
      data-testid={`bundle-history-${bundleKey}`}
    >
      <TableBody>
        {rows.map((c) => {
          const label = FIELD_LABELS[c.field] ?? ((v: boolean | null) => String(v));
          return (
            <TableRow key={c.id}>
              <TableCell sx={{ width: '26%' }}>
                {fmtDate(c.changedAt)}
                <Typography variant="caption" color="text.secondary" component="div">
                  {c.changedByName ?? '—'}
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '34%' }}>
                {label(c.oldValue)} → {label(c.newValue)}
              </TableCell>
              <TableCell sx={{ wordBreak: 'break-word' }}>{c.reason}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function BundleReasonDialog({
  pending,
  onClose,
  onSaved,
}: {
  pending: PendingChange;
  onClose: () => void;
  onSaved: (next: BundlesOverview) => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();
  const tooLong = trimmed.length > MAX_BUNDLE_REASON;
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(await saveBundle(pending.bundle.key, pending.change, trimmed));
    } catch (e) {
      setError(toErrorMessage(e));
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{pending.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {pending.change.enabled === true
            ? `Wszystkie funkcje paczki (${pending.bundle.flags.length}) zaczną działać dla wszystkich od razu.`
            : pending.change.enabled === false
              ? 'Funkcje paczki wrócą do wersji obecnej dla wszystkich od razu.'
              : 'Zmiana dotyczy menu przy wyłączonej paczce; ekrany i ich adresy działają dalej.'}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label="Powód"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={tooLong}
          helperText={
            tooLong ? `Najwyżej ${MAX_BUNDLE_REASON} znaków` : 'Powód trafi do historii paczki i dziennika działań.'
          }
          slotProps={{ htmlInput: { 'data-testid': 'bundle-reason' } }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={saving || trimmed === '' || tooLong}
          onClick={() => void save()}
          data-testid="bundle-reason-save"
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
